/**
 * EO task 2 with the live virtual examiner (P3B-06). While the full recording of the task goes
 * on (kept as before), each turn of the learner is captured on its own: at the end of the turn
 * (« J'ai fini ma phrase » or a pause after speaking), the segment is sent to the server, which
 * transcribes it and answers in the role of the task. The answer is read aloud in French and
 * shown. The server keeps the time and says when the task ends; without AI it answers with the
 * next recorded follow-up. A network error never stops the task: a local follow-up is spoken.
 */

import { academyAttemptsExaminerCreate } from "@agencyos/api-client";
import { type TurnListening, listenTurn, startTurn } from "@agencyos/core";
import { useCallback, useEffect, useRef, useState } from "react";

import { extensionFor, recorderMimeType, speakFrench } from "./audio";
import type { StateTask } from "./types";
import { isNoOralCredits } from "@agencyos/core";
import { reportNoOralCredits } from "./TrainerReview";

/** Microphone level (0 to 1) above which a voice is heard. */
const VOICE_LEVEL = 0.06;

export type LiveStatus = "idle" | "listening" | "sending" | "speaking";

export interface ExaminerLine {
  role: "learner" | "examiner";
  text: string;
}

/** Where a turn goes: the sitting's examiner by default, the redo's otherwise (D-179). */
export type TurnPost = (attemptId: string, body: { task: string; audio: File; offset: number }) => Promise<{ data: { transcript?: string; reply: string; end: boolean } }>;

interface Options {
  attemptId: string;
  task: Pick<StateTask, "id" | "follow_ups"> | undefined;
  stream: MediaStream | null;
  level: number;
  active: boolean;
  onEnd: () => void;
  post?: TurnPost;
  speak?: (text: string) => Promise<void>;
}

export function useLiveExaminer({ attemptId, task, stream, level, active, onEnd, post = academyAttemptsExaminerCreate as TurnPost, speak = speakFrench }: Options) {
  const [status, setStatus] = useState<LiveStatus>("idle");
  const [lines, setLines] = useState<ExaminerLine[]>([]);
  const [offline, setOffline] = useState(false);
  const segment = useRef<MediaRecorder | null>(null);
  const chunks = useRef<Blob[]>([]);
  const listening = useRef<TurnListening>(startTurn(0));
  const segmentStart = useRef(0);
  const taskStart = useRef(0);
  const activeRef = useRef(active);
  const fallbackIndex = useRef(0);
  const endRef = useRef(onEnd);
  const statusRef = useRef<LiveStatus>("idle");

  const changeStatus = (next: LiveStatus) => {
    statusRef.current = next;
    setStatus(next);
  };

  useEffect(() => {
    endRef.current = onEnd;
  }, [onEnd]);

  const startSegment = useCallback(() => {
    if (!stream || !activeRef.current || typeof MediaRecorder === "undefined") return;
    const mimeType = recorderMimeType();
    const media = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
    chunks.current = [];
    media.ondataavailable = (event) => {
      if (event.data.size) chunks.current.push(event.data);
    };
    segment.current = media;
    segmentStart.current = Date.now();
    listening.current = startTurn(segmentStart.current);
    media.start();
    changeStatus("listening");
  }, [stream]);

  const answer = useCallback(
    async (blob: Blob) => {
      if (!task) return;
      changeStatus("sending");
      const type = blob.type || "audio/webm";
      const file = new File([blob], `eo-tour.${extensionFor(type)}`, { type });
      const offset = Math.max((segmentStart.current - taskStart.current) / 1000, 0);
      let reply = "";
      let end = false;
      try {
        const response = await post(attemptId, { task: task.id, audio: file, offset });
        const data = response.data;
        reply = data.reply;
        end = data.end;
        setOffline(false);
        setLines((list) => [
          ...list,
          ...(data.transcript ? [{ role: "learner" as const, text: data.transcript }] : []),
          ...(reply ? [{ role: "examiner" as const, text: reply }] : []),
        ]);
      } catch (error) {
        if (isNoOralCredits(error)) reportNoOralCredits(); // D-124: polite notice, no credit left
        // no network or time over on the server: a recorded follow-up keeps the exchange going
        setOffline(true);
        reply = task.follow_ups[fallbackIndex.current % Math.max(task.follow_ups.length, 1)] ?? "";
        fallbackIndex.current += 1;
        if (reply) setLines((list) => [...list, { role: "examiner", text: reply }]);
      }
      if (!activeRef.current) return;
      if (reply) {
        changeStatus("speaking");
        await speak(reply);
      }
      if (!activeRef.current) return;
      if (end) {
        changeStatus("idle");
        endRef.current();
      } else {
        startSegment();
      }
    },
    [attemptId, task, startSegment, post, speak],
  );

  /** The learner has finished their sentence: the segment goes to the examiner. */
  const finishTurn = useCallback(() => {
    const media = segment.current;
    if (!media || media.state !== "recording" || statusRef.current !== "listening") return;
    media.onstop = () => {
      const blob = new Blob(chunks.current, { type: media.mimeType || "audio/webm" });
      void answer(blob);
    };
    media.stop();
  }, [answer]);

  // Start and stop with the recording of the task (the time is up: nothing more is sent).
  useEffect(() => {
    activeRef.current = active;
    if (active) {
      taskStart.current = Date.now();
      setLines([]);
      startSegment();
      return;
    }
    const media = segment.current;
    if (media && media.state === "recording") {
      media.onstop = null;
      media.stop();
    }
    segment.current = null;
    changeStatus("idle");
  }, [active, startSegment]);

  useEffect(
    () => () => {
      activeRef.current = false;
      const media = segment.current;
      if (media && media.state === "recording") {
        media.onstop = null;
        media.stop();
      }
    },
    [],
  );

  // End of the turn by a pause after speech, or a relaunch after a long silence.
  useEffect(() => {
    if (!active || status !== "listening") return;
    const next = listenTurn(listening.current, level > VOICE_LEVEL, Date.now());
    listening.current = next;
    if (next.end) finishTurn();
  }, [level, active, status, finishTurn]);

  return { status, lines, offline, finishTurn };
}
